import React, { useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.js";
import Tesseract from "tesseract.js";
import axios from "axios";
import "./PdfUploadButton.css";
import axiosInstance from "../api/axiosInstance";

// ✅ WORKER SETUP (Correct for pdfjs 2.16.105 + CRA)
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const BLOCKS_LIST = [
  "Dhenkanal Sadar",
  "Hindol",
  "Parjang",
  "Bhuban",
  "Kamakhyanagar",
  "Odapada",
  "Gondia",
  "Kankadahad",
];

export default function PdfUploadButton() {
  const [showModal, setShowModal] = useState(false);
  const [pdfData, setPdfData] = useState({
    description: "",
    address: "",
    mobile: "",
    name: "",
    janasunaniId: "",
  });

  /* ---------------- STEP 1: PDF TEXT EXTRACTION ---------------- */

  const extractTextFromPdf = async (file) => {
    const buffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;

    let text = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map((item) => item.str).join(" ") + "\n";
    }
    return text.trim();
  };

  /* ---------------- STEP 2: OCR EXTRACTION ---------------- */

  const extractTextFromImage = async (file) => {
    const { data } = await Tesseract.recognize(file, "eng", {
      logger: (m) => console.log(m),
    });
    return data.text.trim();
  };

  /* ---------------- STEP 3: NORMALIZE TEXT ---------------- */

  const normalizeText = (text) =>
    text
      .replace(/\r?\n+/g, "\n")
      .replace(/[|]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  /* ---------------- STEP 4: FIELD PARSING ---------------- */

  const KNOWN_KEYS = [
    "Registration No.",
    "Received By",
    "Received Date",
    "Contact No",
    "Email Address",
    "Address",
    "Current Status",
    "Grievance Details",
    "Action History",
    "Name",
    "Mobile",
  ];

  const cleanFieldValue = (value) => {
    return value
      .replace(/^(Address|Mobile|Contact No)\s*[:\-]*/i, "")
      .replace(/^[:\s]+/, "")
      .replace(/[:\s]+$/, "")
      .replace(/\s{2,}/g, " ")
      .trim();
  };

  const extractField = (text, label) => {
    const otherKeys = KNOWN_KEYS.filter(
      (k) => k.toLowerCase() !== label.toLowerCase()
    );

    const stopRegex = otherKeys
      .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|");

    const pattern = new RegExp(
      `${label}\\s*[:\\-]?\\s*(.+?)(?=\\s*(?:${stopRegex})|$)`,
      "i"
    );

    const match = text.match(pattern);
    return match ? cleanFieldValue(match[1]) : "";
  };

  /* ---------------- STEP 5: FILE UPLOAD HANDLER ---------------- */

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      let extractedText = "";

      if (file.type === "application/pdf") {
        extractedText = await extractTextFromPdf(file);

        // 🔁 fallback to OCR if PDF text empty
        if (!extractedText || extractedText.length < 30) {
          extractedText = await extractTextFromImage(file);
        }
      } else if (file.type.startsWith("image/")) {
        extractedText = await extractTextFromImage(file);
      }

      const cleanText = normalizeText(extractedText);

      const registrationNo = extractField(cleanText, "Registration No.")
        .replace(/^[^A-Za-z0-9]+/, "")
        .trim();

      const rawMobile =
        extractField(cleanText, "Mobile") ||
        extractField(cleanText, "Contact No");

      setPdfData({
        description: extractField(cleanText, "Grievance Details"),
        address: extractField(cleanText, "Address"),
        mobile: rawMobile.replace(/\D/g, ""),
        name: extractField(cleanText, "Name"),
        janasunaniId: registrationNo,
      });
    } catch (err) {
      console.error("Extraction error:", err);
      alert("Unable to read file. Please upload valid PDF or image.");
    }
  };

  /* ---------------- INPUT HANDLER ---------------- */

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPdfData((prev) => ({ ...prev, [name]: value }));
  };

  /* ---------------- SUBMIT HANDLER ---------------- */

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("Session expired. Please login again.");
        return;
      }

      const payload = {
        janasunaniId: pdfData.janasunaniId,
        originalFiledDate: new Date().toISOString(),
        mobile: pdfData.mobile,
        name: pdfData.name,
        description: pdfData.description,
        userCategory: "other",
      };

      const res = await axios.post(
        axiosInstance.get("/grievances/import"),
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data?.success) {
        setPdfData({
          description: "",
          address: "",
          mobile: "",
          name: "",
          janasunaniId: "",
        });

        alert(res.data?.message || "Grievance submitted successfully");
        setShowModal(false);
      } else {
        alert(res.data?.error?.message || "Submission failed");
      }
    } catch (err) {
      console.error("Submission error:", err);

      if (err.response?.status === 401) {
        alert("Session expired. Please login again.");
        localStorage.removeItem("authToken");
        return;
      }

      alert(
        err.response?.data?.error?.message ||
          "Server error. Please try again later."
      );
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <>
      <div className="upload-container">
        <div className="upload-left">
          <h2>Upload Janasunani PDF</h2>
          <p>If the PDF comes from Janasunani, upload here.</p>
        </div>
        <div className="upload-right">
          <button className="upload-btn" onClick={() => setShowModal(true)}>
            Upload Pdf
          </button>
        </div>
      </div>

      {showModal && (
        <div className="pdf-modal-overlay">
          <form className="pdf-modal" onSubmit={handleSubmit}>
            <div className="pdf-modal-header">
              <h3>Upload Janasunani PDF</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowModal(false)}
              >
                ✕
              </button>
            </div>

            <input
              type="file"
              accept="application/pdf,image/*"
              className="file-upload"
              onChange={handlePdfUpload}
            />
 
            <div className="pdf-form">
              <label><b>Janasunani ID</b></label>
              <input name="janasunaniId" value={pdfData.janasunaniId} onChange={handleInputChange} />

              <label><b>Name</b></label>
              <input name="name" value={pdfData.name} onChange={handleInputChange} />

              <label><b>Mobile</b></label>
              <input name="mobile" value={pdfData.mobile} onChange={handleInputChange} />

              <label><b>Grievance Details</b></label>
              <textarea name="description" value={pdfData.description} onChange={handleInputChange} rows={4} />

              <label><b>Address</b></label>
              <input list="blocks" name="address" value={pdfData.address} onChange={handleInputChange} />
              <datalist id="blocks">
                {BLOCKS_LIST.map((block, idx) => (
                  <option key={idx} value={block} />
                ))}
              </datalist>
            </div>

            <div className="modal-actions">
              <button className="submit-btn" type="submit">
                Submit
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}